/**
 * AI Page
 * 
 * Main page for AI-powered features in TMOS
 */

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../state/language/LanguageProvider';
import { AIRoadmapGenerator } from '../../components/ai/AIRoadmapGenerator';
import { AISettingsPage } from './AISettingsPage';
import { CorporateMemoryPage } from './CorporateMemoryPage';
import PromptManagementPage from './PromptManagementPage';
import ModelUsagePage from './ModelUsagePage';

interface TabItem {
  id: string;
  label: string;
  component: React.ReactNode;
}

const AIPage: React.FC = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('roadmap-generator');

  // Set active tab based on URL
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/ai/prompt-management')) {
      setActiveTab('prompt-management');
    } else if (path.includes('/ai/corporate-memory')) {
      setActiveTab('corporate-memory');
    } else if (path.includes('/ai/settings')) {
      setActiveTab('ai-settings');
    } else if (path.includes('/ai/model-usage')) {
      setActiveTab('model-usage');
    } else if (path.includes('/ai/roadmap-generator')) {
      setActiveTab('roadmap-generator');
    } else {
      setActiveTab('roadmap-generator'); // Default tab
    }
  }, [location.pathname]);

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    // Navigate to the appropriate route
    switch (tabId) {
      case 'roadmap-generator':
        navigate('/ai/roadmap-generator');
        break;
      case 'corporate-memory':
        navigate('/ai/corporate-memory');
        break;
      case 'prompt-management':
        navigate('/ai/prompt-management');
        break;
      case 'ai-settings':
        navigate('/ai/settings');
        break;
      case 'model-usage':
        navigate('/ai/model-usage');
        break;
      default:
        navigate('/ai');
    }
  };

  const tabs: TabItem[] = [
    {
      id: 'roadmap-generator',
      label: t('ai.tabs.roadmapGenerator'),
      component: <AIRoadmapGenerator />
    },
    {
      id: 'corporate-memory',
      label: t('ai.tabs.corporateMemory'),
      component: <CorporateMemoryPage />
    },
    {
      id: 'prompt-management',
      label: 'Prompt Management',
      component: <PromptManagementPage />
    },
    {
      id: 'ai-settings',
      label: t('ai.tabs.settings'),
      component: <AISettingsPage />
    },
    {
      id: 'model-usage',
      label: 'Model usage',
      component: <ModelUsagePage />
    }
  ];

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-sm border border-[var(--color-border)] p-4">
          {/* Tab Navigation - consistent with CRM and Roadmaps */}
          <div className="tab-bar rounded-t-lg mb-2">
            <nav className="flex gap-1 overflow-x-auto items-end" aria-label="AI tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={`tab-button inline-flex items-center ${activeTab === tab.id ? 'tab-button-selected' : ''}`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="bg-[var(--color-surface-bg)] rounded-lg border border-[var(--color-border)]">
            {tabs.find(tab => tab.id === activeTab)?.component}
          </div>
        </div>
      </div>
    </div>
  );
};

export { AIPage };
